import { isAxiosError } from 'axios'
import { AlertCircle, Loader2, Minus, Plus, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  InventoryAllocation,
  InventorySource,
  InventorySourcesSnapshot,
  Product,
  posCartService,
} from '../../../services/pos.service'
import { Button } from '../../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog'
import { Input } from '../../ui/input'

export interface InventorySelectionResult {
  quantity: number
  allocations: InventoryAllocation[]
}

export type InventoryQuantityMode = 'derived' | 'fixed'

interface InventorySourceModalProps {
  open: boolean
  product: Pick<Product, 'id' | 'name'> | null
  quantity: number
  quantityMode?: InventoryQuantityMode
  currentQuantity?: number
  currentAllocations?: InventoryAllocation[]
  allowExpired?: boolean
  loadSources?: (productId: number, quantity: number) => Promise<InventorySourcesSnapshot>
  onOpenChange: (open: boolean) => void
  onConfirm: (selection: InventorySelectionResult) => Promise<void> | void
}

const EMPTY_ALLOCATIONS: InventoryAllocation[] = []
const keyOf = (sourceType: string, batchId?: number | null) => `${sourceType}:${batchId ?? 0}`
const selectableSources = (snapshot: InventorySourcesSnapshot, allowExpired: boolean) =>
  allowExpired ? snapshot.sources : snapshot.sources.filter((source) => !source.is_expired)

function sourceMaximum(source: InventorySource, allowExpired: boolean): number {
  if (allowExpired && source.is_expired) {
    return Math.max(0, source.physical - (source.reserved_other ?? source.reserved))
  }

  return Math.max(0, Number(source.available_to_cart ?? source.available))
}

function allocationValues(
  snapshot: InventorySourcesSnapshot,
  allocations: InventoryAllocation[],
  allowExpired: boolean,
): Record<string, number> {
  return Object.fromEntries(selectableSources(snapshot, allowExpired).map((source) => {
    const requested = Number(allocations.find((allocation) =>
      allocation.source_type === source.source_type && allocation.batch_id === source.batch_id,
    )?.quantity ?? 0)
    const maximum = sourceMaximum(source, allowExpired)

    return [keyOf(source.source_type, source.batch_id), Math.max(0, Math.min(maximum, Math.trunc(requested) || 0))]
  }))
}

function InventoryMetric({ label, value }: { label: string; value: number }) {
  return (
    <div data-slot="inventory-metric" className="grid min-w-0 grid-rows-[2.5rem_auto]">
      <span className="flex items-end leading-tight text-muted-foreground">{label}</span>
      <strong className="mt-1 block leading-none">{value}</strong>
    </div>
  )
}

export function InventorySourceModal({
  open,
  product,
  quantity,
  quantityMode = 'fixed',
  currentQuantity = 0,
  currentAllocations = EMPTY_ALLOCATIONS,
  allowExpired = false,
  loadSources,
  onOpenChange,
  onConfirm,
}: InventorySourceModalProps) {
  const productId = product?.id
  const currentAllocationsRef = useRef(currentAllocations)
  const requestRef = useRef(0)
  currentAllocationsRef.current = currentAllocations

  const [snapshot, setSnapshot] = useState<InventorySourcesSnapshot | null>(null)
  const [values, setValues] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (
    clearError = true,
    preserveValues = false,
    requestedQuantity = quantity,
  ) => {
    if (!open || !productId) return

    const requestId = ++requestRef.current
    setLoading(true)
    if (clearError) setError('')
    try {
      const request = loadSources ?? posCartService.getInventorySources
      const data = await request(productId, Math.max(1, Math.trunc(requestedQuantity) || 1))
      if (requestId !== requestRef.current) return
      setSnapshot(data)
      setValues((current) => {
        if (preserveValues) {
          const currentAsAllocations = selectableSources(data, allowExpired).flatMap((source) => {
            const amount = current[keyOf(source.source_type, source.batch_id)] ?? 0
            return amount > 0 ? [{
              source_type: source.source_type,
              batch_id: source.batch_id,
              quantity: amount,
            } satisfies InventoryAllocation] : []
          })
          return allocationValues(data, currentAsAllocations, allowExpired)
        }

        const initial = currentAllocationsRef.current.length > 0
          ? currentAllocationsRef.current
          : data.proposed_allocations
        return allocationValues(data, initial, allowExpired)
      })
    } catch (caught: unknown) {
      if (requestId !== requestRef.current) return
      if (!preserveValues) setSnapshot(null)
      if (clearError) {
        const message = isAxiosError<{ message?: string }>(caught) ? caught.response?.data?.message : undefined
        setError(message || 'No fue posible consultar los lotes.')
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false)
    }
  }, [allowExpired, loadSources, open, productId, quantity])

  useEffect(() => {
    if (!open || !productId) return
    setSnapshot(null)
    setValues({})
    void load(true, false, quantity)
    return () => {
      requestRef.current += 1
    }
  }, [load, open, productId, quantity])

  const allocated = useMemo(
    () => Object.values(values).reduce((sum, value) => sum + (Number(value) || 0), 0),
    [values],
  )
  const fixedQuantity = Math.max(1, Math.trunc(quantity) || 1)
  const visibleSources = useMemo(
    () => snapshot ? selectableSources(snapshot, allowExpired) : [],
    [allowExpired, snapshot],
  )
  const maximumQuantity = useMemo(
    () => visibleSources.reduce((sum, source) => sum + sourceMaximum(source, allowExpired), 0),
    [allowExpired, visibleSources],
  )
  const excludedExpiredPhysical = useMemo(() => {
    if (!snapshot || allowExpired) return 0
    return snapshot.excluded_expired_physical
      ?? snapshot.sources.filter((source) => source.is_expired).reduce((sum, source) => sum + source.physical, 0)
  }, [allowExpired, snapshot])
  const excludedExpiredReservedHere = !allowExpired
    ? (snapshot?.excluded_expired_reserved_by_cart ?? 0)
    : 0
  const selectedQuantity = quantityMode === 'derived' ? allocated : fixedQuantity
  const deletesExistingLine = quantityMode === 'derived' && currentQuantity > 0 && allocated === 0
  const canConfirm = Boolean(snapshot) && !loading && !saving && (
    quantityMode === 'fixed'
      ? allocated === fixedQuantity
      : allocated > 0 || deletesExistingLine
  )

  const updateSource = (key: string, value: number, maximum: number) => {
    const next = Math.max(0, Math.min(maximum, Math.trunc(value) || 0))
    setValues((current) => ({ ...current, [key]: next }))
  }

  const confirm = async () => {
    if (!snapshot) return
    if (quantityMode === 'fixed' && allocated !== fixedQuantity) {
      toast.error(`Distribuye exactamente ${fixedQuantity} unidad${fixedQuantity === 1 ? '' : 'es'}.`)
      return
    }
    if (quantityMode === 'derived' && allocated === 0 && currentQuantity === 0) {
      toast.error('Selecciona al menos una unidad.')
      return
    }

    const allocations = visibleSources.flatMap((source) => {
      const amount = values[keyOf(source.source_type, source.batch_id)] || 0
      return amount > 0 ? [{
        source_type: source.source_type,
        batch_id: source.batch_id,
        quantity: amount,
      } satisfies InventoryAllocation] : []
    })

    setSaving(true)
    setError('')
    try {
      await onConfirm({ quantity: selectedQuantity, allocations })
      onOpenChange(false)
    } catch (caught: unknown) {
      const message = isAxiosError<{ message?: string }>(caught) ? caught.response?.data?.message : undefined
      setError(message || 'No fue posible reservar el inventario seleccionado.')
      await load(false, true, selectedQuantity)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] min-h-0 max-w-2xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Seleccionar origen de inventario</DialogTitle>
          <DialogDescription>
            {product?.name}. Se conserva la distribución vigente y se completa con stock libre y después FEFO.
          </DialogDescription>
        </DialogHeader>

        <div
          data-slot="inventory-source-body"
          className="min-h-0 min-w-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto pr-1"
        >
          {quantityMode === 'fixed' && (
            <div className="flex items-center justify-between rounded-lg border bg-slate-50 p-3 text-sm">
              <span className="text-muted-foreground">Cantidad a distribuir</span>
              <strong>{fixedQuantity} unidad{fixedQuantity === 1 ? '' : 'es'}</strong>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <span className="flex gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{error}</span>
              <Button type="button" size="sm" variant="outline" onClick={() => void load()} disabled={loading || saving}>
                <RefreshCw className="mr-2 h-4 w-4" />Reintentar
              </Button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : snapshot ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted p-3 text-sm sm:grid-cols-4">
                <InventoryMetric label="Físico" value={snapshot.physical} />
                <InventoryMetric label="Reservado por otros" value={snapshot.reserved_other ?? snapshot.reserved} />
                <InventoryMetric label="Reservado aquí" value={snapshot.reserved_by_cart ?? 0} />
                <InventoryMetric label="Disponible aquí" value={snapshot.sellable_to_cart ?? snapshot.sellable} />
              </div>

              {excludedExpiredPhysical > 0 && (
                <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    {excludedExpiredPhysical} unidad{excludedExpiredPhysical === 1 ? '' : 'es'} caducada{excludedExpiredPhysical === 1 ? '' : 's'}
                    {' '}se mantiene{excludedExpiredPhysical === 1 ? '' : 'n'} en el inventario físico, pero no
                    {' '}está{excludedExpiredPhysical === 1 ? '' : 'n'} disponible{excludedExpiredPhysical === 1 ? '' : 's'} ni se muestra{excludedExpiredPhysical === 1 ? '' : 'n'} como origen.
                    {excludedExpiredReservedHere > 0
                      ? ` ${excludedExpiredReservedHere} unidad${excludedExpiredReservedHere === 1 ? '' : 'es'} reservada${excludedExpiredReservedHere === 1 ? '' : 's'} por este carrito debe${excludedExpiredReservedHere === 1 ? '' : 'n'} redistribuirse o eliminarse.`
                      : ''}
                  </span>
                </div>
              )}

              <div data-slot="inventory-source-list" className="space-y-2">
                {visibleSources.map((source) => {
                  const key = keyOf(source.source_type, source.batch_id)
                  const label = source.source_type === 'unbatched' ? 'Stock libre' : `Lote ${source.batch_number}`
                  const maximum = sourceMaximum(source, allowExpired)
                  const amount = values[key] ?? 0
                  const disabled = saving || (source.is_expired && !allowExpired)

                  return (
                    <div key={key} className="grid items-center gap-3 rounded-lg border p-3 sm:grid-cols-[1fr_auto]">
                      <div>
                        <div className="font-medium">{label}</div>
                        <div className="text-xs text-muted-foreground">
                          {source.expiration_date ? `Caduca ${source.expiration_date} · ` : ''}
                          físico {source.physical} · otros {source.reserved_other ?? source.reserved} · disponible {maximum}
                          {source.is_expired ? ' · CADUCADO' : ''}
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          aria-label={`Disminuir ${label}`}
                          disabled={disabled || amount <= 0}
                          onClick={() => updateSource(key, amount - 1, maximum)}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <Input
                          type="number"
                          min={0}
                          max={maximum}
                          step={1}
                          aria-label={`Cantidad ${label}`}
                          className="w-20 text-center"
                          disabled={disabled}
                          value={amount}
                          onChange={(event) => updateSource(key, Number(event.target.value), maximum)}
                        />
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          aria-label={`Aumentar ${label}`}
                          disabled={disabled || amount >= maximum}
                          onClick={() => updateSource(key, amount + 1, maximum)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}
        </div>

        {snapshot && !loading && (
          <div data-slot="inventory-source-footer" className="flex shrink-0 items-center justify-between border-t pt-4">
            {quantityMode === 'fixed' ? (
              <span className={allocated === fixedQuantity ? 'text-sm text-emerald-600' : 'text-sm text-amber-600'}>
                Distribuido {allocated} de {fixedQuantity}
              </span>
            ) : (
              <span className="text-sm text-slate-600">Cantidad seleccionada: {allocated} · máximo {maximumQuantity}</span>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
              <Button
                onClick={() => void confirm()}
                disabled={!canConfirm}
                className={deletesExistingLine ? 'bg-red-600 hover:bg-red-700' : undefined}
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {deletesExistingLine ? 'Eliminar del carrito' : 'Confirmar'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
